import React, { Children } from 'react';
import { motion } from 'framer-motion';
import type { AppState, View } from '../types';
import { StatCard, T, useIsMobile } from '../ui';
export function Dashboard({
  state
}: {
  state: AppState;
  setView: (v: View) => void;
}) {
  const isMobile = useIsMobile();
  // Resumen de consumo: último período por sucursal (diferencia de lecturas).
  const resumenConsumo = state.sucs.map(s => {
    const lects = state.consumos.filter(c => c.sucursalId === s.id).sort((a, b) => a.fecha - b.fecha);
    const ultima = lects[lects.length - 1];
    const previa = lects[lects.length - 2];
    const periodo = ultima ? previa ? Math.max(ultima.kwh - previa.kwh, 0) : ultima.kwh : 0;
    return {
      id: s.id,
      periodo,
      acumulado: ultima?.kwh ?? 0
    };
  });
  const totalPeriodoGlobal = resumenConsumo.reduce((a, r) => a + r.periodo, 0);
  const mayor = [...resumenConsumo].sort((a, b) => b.periodo - a.periodo)[0];
  const sucMayor = state.sucs.find(s => s.id === mayor?.id)?.nombre ?? '—';
  const lecturasRegistradas = state.consumos.length;
  const fmt = (n: number) => n.toLocaleString('es-DO');
  return <>
      <style data-id="element-510" data-mp="3o7t2weohme3n">{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@600;700&family=DM+Sans:wght@400;500;600;700&display=swap');
        .dash * { font-family: 'DM Sans', sans-serif; }
        .dash-title { font-family: 'Space Grotesk', sans-serif; }
        .mod-card {
          background: ${T.surface};
          border: 1px solid ${T.border};
          border-radius: ${T.rLg}px;
          box-shadow: ${T.innerHi};
          padding: 20px;
          text-align: left;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          transition: border-color .22s, box-shadow .22s, transform .22s;
        }
        .mod-card:hover { border-color: ${T.borderStrong}; box-shadow: 0 14px 40px rgba(0,0,0,.08), ${T.innerHi}; transform: translateY(-3px); }
        .mod-card .mod-arrow { transition: transform .2s; }
        .mod-card:hover .mod-arrow { transform: translateX(4px); }
        .rec-row { display: grid; grid-template-columns: 1.4fr 1.3fr 0.8fr 1fr 0.8fr; padding: 13px 16px; align-items: center; border-bottom: 1px solid ${T.border}; transition: background .15s; }
        .rec-row:last-child { border-bottom: none; }
        .rec-body .rec-row:hover { background: ${T.surfaceHover}; }
      `}</style>

      <div className="dash" data-id="element-511" data-mp="34m7mrvuteb3o">
        {/* ── HEADER ── */}
        <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        ease: [0.2, 0.8, 0.2, 1]
      }} style={{
        marginBottom: 24,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        gap: 20,
        flexWrap: 'wrap'
      }} data-id="element-512" data-mp="39k952ekf26e3">
          
          <div data-id="element-513" data-mp="2pw0a5wrhnmaw">
            <div style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '.18em',
            color: T.accent,
            textTransform: 'uppercase',
            marginBottom: 9,
            display: 'flex',
            alignItems: 'center',
            gap: 7
          }} data-id="element-514" data-mp="2pvgruexxevwc">
              
              <span style={{
              width: 16,
              height: 2,
              borderRadius: 2,
              background: T.accent,
              boxShadow: `0 0 10px ${T.accent}`
            }} data-id="element-515" data-mp="29aq87jyp1pqi" />
              
              Sistema GETR
            </div>
            <div className="dash-title" style={{
            fontSize: isMobile ? 24 : 34,
            fontWeight: 800,
            letterSpacing: '-0.03em',
            color: T.text,
            lineHeight: 1.05
          }} data-id="element-516" data-mp="1umn1g8ctua0n">
              
              Resumen general
            </div>
            <div style={{
            fontSize: 13,
            color: T.textSecondary,
            marginTop: 8,
            fontWeight: 500
          }} data-id="element-517" data-mp="1gnx0ghmvzq70">
              
              {state.sucs.length} sucursales · {lecturasRegistradas} lecturas de
              consumo registradas
            </div>
          </div>
          <div style={{
          background: T.okSoft,
          border: `1px solid rgba(74,222,128,.3)`,
          borderRadius: 99,
          padding: '8px 16px',
          fontSize: 13,
          fontWeight: 700,
          color: T.ok,
          display: 'flex',
          alignItems: 'center',
          gap: 7
        }} data-id="element-518" data-mp="3golgagrf6bsm">
            
            <span style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: T.ok,
            boxShadow: `0 0 8px ${T.ok}`
          }} data-id="element-519" data-mp="2t25a3jmsrilm" />
            
            {fmt(totalPeriodoGlobal)} kWh último período
          </div>
        </motion.div>

        {/* ── KPIs semáforo ── */}
        <motion.div initial="hidden" animate="show" variants={{
        hidden: {},
        show: {
          transition: {
            staggerChildren: 0.05
          }
        }
      }} style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: isMobile ? 10 : 12,
        marginBottom: isMobile ? 20 : 28
      }} data-id="element-520" data-mp="harl36f4yqkb">
          
          {[{
          label: 'Sucursales',
          value: String(state.sucs.length),
          note: 'Puntos comerciales',
          accent: T.info
        }, {
          label: 'Consumo período',
          value: `${fmt(totalPeriodoGlobal)} kWh`,
          note: 'Último período registrado',
          accent: T.accent
        }, {
          label: 'Mayor consumo',
          value: sucMayor,
          note: mayor ? `${fmt(mayor.periodo)} kWh` : 'Sin datos',
          accent: T.warn
        }, {
          label: 'Lecturas',
          value: String(lecturasRegistradas),
          note: 'Registros de medidor',
          accent: T.ok
        }].map(k => <motion.div key={k.label} variants={{
          hidden: {
            opacity: 0,
            y: 14
          },
          show: {
            opacity: 1,
            y: 0,
            transition: {
              duration: 0.34,
              ease: [0.2, 0.8, 0.2, 1]
            }
          }
        }} data-id="element-521" data-mp="1544ejv7qn8vp">
            
              <StatCard label={k.label} value={k.value} note={k.note} accent={k.accent} data-id="element-522" data-mp="1yrr2nv0lo2iv" />
            
            </motion.div>)}
        </motion.div>

        {/* ── Consumo por sucursal ── */}
        <div style={{
        fontSize: 11,
        fontWeight: 700,
        color: T.textMuted,
        letterSpacing: '.1em',
        textTransform: 'uppercase',
        marginBottom: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 8
      }} data-id="element-523" data-mp="28qq3wsj1cvvu">
          
          <span style={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          background: T.accent
        }} data-id="element-524" data-mp="2mhlce3pmhayw" />
          
          Consumo por sucursal
        </div>
        {(() => {
        // Lecturas del medidor (kWh acumulados). El consumo del último período
        // es la diferencia entre las dos lecturas más recientes por sucursal.
        const resumen = state.sucs.map(s => {
          const lects = state.consumos.filter(c => c.sucursalId === s.id).sort((a, b) => a.fecha - b.fecha);
          const ultima = lects[lects.length - 1];
          const previa = lects[lects.length - 2];
          const periodo = ultima ? previa ? Math.max(ultima.kwh - previa.kwh, 0) : ultima.kwh : 0;
          return {
            id: s.id,
            nombre: s.nombre,
            periodo,
            acumulado: ultima?.kwh ?? 0,
            fecha: ultima?.fecha ?? 0
          };
        }).sort((a, b) => b.periodo - a.periodo);
        const maxPeriodo = Math.max(...resumen.map(r => r.periodo), 1);
        const totalPeriodo = resumen.reduce((a, r) => a + r.periodo, 0);
        const fmt = (n: number) => n.toLocaleString('es-DO');
        const conDatos = resumen.some(r => r.acumulado > 0);
        return <div style={{
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: T.rLg,
          overflow: 'hidden',
          boxShadow: T.innerHi
        }} data-id="element-525" data-mp="1hhn52rvaeu5q">
              
              {/* total banner */}
              <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '14px 18px',
            background: T.surfaceSubtle,
            borderBottom: `1px solid ${T.border}`
          }} data-id="element-526" data-mp="guf86xazixmm">
                
                <span style={{
              fontSize: 10,
              color: T.textMuted,
              fontWeight: 700,
              letterSpacing: '.08em',
              textTransform: 'uppercase'
            }} data-id="element-527" data-mp="19h9rvrzz9sz1">
                  
                  Último período
                </span>
                <span style={{
              fontSize: 15,
              fontWeight: 800,
              color: T.accent,
              fontFamily: "'Space Grotesk', sans-serif"
            }} data-id="element-528" data-mp="14luef15vtz35">
                  
                  {fmt(totalPeriodo)} kWh
                </span>
              </div>
              {!conDatos && <div style={{
            padding: '28px 16px',
            textAlign: 'center',
            color: T.textMuted,
            fontSize: 13
          }} data-id="element-529" data-mp="3crmtn1hod9dm">
                
                  Aún no hay lecturas de consumo registradas
                </div>}
              {conDatos && resumen.map((r, i) => <motion.div key={r.id} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.32,
            delay: i * 0.05,
            ease: [0.2, 0.8, 0.2, 1]
          }} style={{
            padding: '14px 18px',
            borderBottom: i < resumen.length - 1 ? `1px solid ${T.border}` : 'none'
          }} data-id="element-530" data-mp="2y2qc8dtqak0c" data-mpi={i}>
                
                    <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 8,
              gap: 12
            }} data-id="element-531" data-mp="2faxrf6otc53p">
                  
                      <span style={{
                fontSize: 13,
                fontWeight: 600,
                color: T.text
              }} data-id="element-532" data-mp="2h3niegrs7d9r">
                    
                        {r.nombre}
                      </span>
                      <span style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 6
              }} data-id="element-533" data-mp="2370c3ndxvar6">
                    
                        <span style={{
                  fontSize: 15,
                  fontWeight: 800,
                  color: T.text,
                  fontFamily: "'Space Grotesk', sans-serif"
                }} data-id="element-534" data-mp="1tgfheb1dyqdj">
                      
                          {fmt(r.periodo)}
                        </span>
                        <span style={{
                  fontSize: 11,
                  color: T.textMuted,
                  fontWeight: 600
                }} data-id="element-535" data-mp="1fh5yh3lspi5v">
                      
                          kWh
                        </span>
                      </span>
                    </div>
                    {/* bar */}
                    <div style={{
              height: 7,
              borderRadius: 99,
              background: T.surfaceSubtle,
              overflow: 'hidden'
            }} data-id="element-536" data-mp="3r2olsnk4cpyr">
                  
                      <motion.div initial={{
                width: 0
              }} animate={{
                width: `${Math.round(r.periodo / maxPeriodo * 100)}%`
              }} transition={{
                duration: 0.6,
                delay: 0.15 + i * 0.05,
                ease: [0.2, 0.8, 0.2, 1]
              }} style={{
                height: '100%',
                borderRadius: 99,
                background: `linear-gradient(90deg, ${T.accent}, #FF8C00)`,
                boxShadow: `0 0 12px ${T.accent}66`
              }} data-id="element-537" data-mp="3yrzazbqw2sw" />
                  
                    </div>
                    <div style={{
              marginTop: 6,
              fontSize: 11,
              color: T.textMuted
            }} data-id="element-538" data-mp="2oo7vyaxnalqg">
                  
                      Lectura acumulada {fmt(r.acumulado)} kWh
                      {r.fecha ? ` · ${new Date(r.fecha).toLocaleDateString('es-DO', {
                month: 'short',
                year: 'numeric'
              })}` : ''}
                    </div>
                  </motion.div>)}
            </div>;
      })()}
      </div>
    </>;
}